/**
 * Client typé pour les routes `/tasks` — Phase 4, incrément 4.7,
 * étendu **offline-first** en Phase 3.3.3.
 *
 * Pattern identique à `api/events.ts` (cf. 3.3.2) : les mutations
 * retournent `SendResult<T>` ; la lecture reste un `apiRequest`
 * direct.
 */
import { apiRequest } from './client.js';
import { sendOrQueueMutation, type SendResult } from './mutationsReplay.js';

export interface ApiTask {
  id: string;
  homeId: string;
  text: string;
  /** Format `YYYY-MM-DD` ou `null` si pas d'échéance. */
  due: string | null;
  done: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskInput {
  text: string;
  /** Format `YYYY-MM-DD`. Omis = task sans échéance. */
  due?: string;
  done?: boolean;
}

/**
 * Update partiel. `null` accepté sur `due` pour reset. `done`
 * accepte boolean explicite (toggle = passer la valeur cible).
 */
export interface UpdateTaskInput {
  text?: string;
  due?: string | null;
  done?: boolean;
}

export async function listTasks(homeId: string): Promise<ApiTask[]> {
  const data = await apiRequest<{ tasks: ApiTask[] }>(`/homes/${homeId}/tasks`);
  return data.tasks;
}

export async function createTask(
  homeId: string,
  input: CreateTaskInput,
  localId?: string,
): Promise<SendResult<{ task: ApiTask }>> {
  return sendOrQueueMutation<{ task: ApiTask }>({
    method: 'POST',
    path: `/homes/${homeId}/tasks`,
    body: input,
    ...(localId ? { localId } : {}),
  });
}

export async function updateTask(
  id: string,
  input: UpdateTaskInput,
): Promise<SendResult<{ task: ApiTask }>> {
  return sendOrQueueMutation<{ task: ApiTask }>({
    method: 'PATCH',
    path: `/tasks/${id}`,
    body: input,
  });
}

export async function deleteTask(id: string): Promise<SendResult<void>> {
  return sendOrQueueMutation<void>({
    method: 'DELETE',
    path: `/tasks/${id}`,
  });
}
